// client/src/components/ticketing/CheckinConsole.jsx
//
// Staff-facing check-in console: camera QR scan, comp walk-in registration,
// and a roster with manual override. Mobile-first — this is the screen
// staff use on a phone at the door. Camera scanning uses html5-qrcode's
// bare Html5Qrcode class (not the end-to-end Html5QrcodeScanner) so the
// scan viewport can fill the tab and its permission/file-scan controls can
// live in the app footer instead of the library's own inline buttons.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { FaCamera } from 'react-icons/fa';
import { MdDocumentScanner } from 'react-icons/md';
import './CheckinConsole.css';

const EMPTY_WALKIN = { name: '', email: '', phone: '', tier_id: '', num_tickets: 1 };
const ROSTER_LIMIT = 50;

// Scan outcomes. Each gets its own icon, heading and surface colour so staff
// can tell a duplicate from an invalid ticket at a glance (not by colour alone),
// plus a distinct vibration pattern where the device supports it.
const SCAN_OUTCOMES = {
  ok: { icon: '✓', title: 'Checked in', note: 'ok', vibrate: 80 },
  duplicate: { icon: '⚠', title: 'Already checked in', note: 'warn', vibrate: [80, 60, 80, 60, 80] },
  inactive: { icon: '✗', title: 'Not valid for entry', note: 'error', vibrate: 400 },
  not_found: { icon: '✗', title: 'Ticket not recognised', note: 'error', vibrate: 400 },
  error: { icon: '!', title: 'Scan failed', note: 'error', vibrate: 400 },
};

function vibrate(pattern) {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') navigator.vibrate(pattern);
  } catch (_) {
    // unsupported / blocked — the on-screen result is the primary signal
  }
}

function formatTime(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function ticketLabel(ticket) {
  if (!ticket) return '';
  return [ticket.attendee_name, ticket.tier_name].filter(Boolean).join(' — ');
}

// Maps a scan response (success body, or error body + HTTP status) to an outcome.
function toScanResult(data, httpStatus) {
  if (data && data.success) {
    return { kind: 'ok', detail: ticketLabel(data.ticket), ticket: data.ticket };
  }
  const reason = data && data.reason;
  if (reason === 'already_checked_in' || httpStatus === 409) {
    const at = formatTime(data && data.ticket && data.ticket.checked_in_at);
    const who = ticketLabel(data && data.ticket);
    const detail = [who, at && `at ${at}`].filter(Boolean).join(' · ');
    return { kind: 'duplicate', detail: detail || data?.message, ticket: data?.ticket };
  }
  if (reason === 'not_found' || httpStatus === 404) {
    return { kind: 'not_found', detail: data?.message || 'This QR code is not a ticket for this event.' };
  }
  if (reason === 'inactive' || httpStatus === 400) {
    return { kind: 'inactive', detail: data?.message };
  }
  return { kind: 'error', detail: data?.message || 'Check your connection and try again.' };
}

export default function CheckinConsole({ tenantId, eventId, onExtraFooterButtons }) {
  const base = `/api/ticketing/${tenantId}/event/${eventId}`;

  const [tab, setTab] = useState('scan'); // scan | walkin | roster
  const [scanResult, setScanResult] = useState(null); // { kind, detail, ticket }
  const [cameraOn, setCameraOn] = useState(false);
  const scanBusyRef = useRef(false);
  const scannerRef = useRef(null); // Html5Qrcode instance, created lazily
  const fileInputRef = useRef(null);

  const [walkinForm, setWalkinForm] = useState(EMPTY_WALKIN);
  const [walkinSaving, setWalkinSaving] = useState(false);
  const [walkinMessage, setWalkinMessage] = useState(null);
  const [tiers, setTiers] = useState(null); // null = not loaded yet
  const [tiersError, setTiersError] = useState(null);

  const [roster, setRoster] = useState([]);
  const [rosterTotal, setRosterTotal] = useState(0);
  const [rosterStatus, setRosterStatus] = useState('');
  const [rosterPage, setRosterPage] = useState(1);
  const [rosterSearch, setRosterSearch] = useState('');
  const [rosterQuery, setRosterQuery] = useState(''); // debounced rosterSearch
  const [rosterError, setRosterError] = useState(null);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [overrideBusyId, setOverrideBusyId] = useState(null);

  // Debounce typing in the roster search box before hitting the API.
  useEffect(() => {
    const id = setTimeout(() => {
      setRosterQuery(rosterSearch.trim());
      setRosterPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [rosterSearch]);

  const loadRoster = useCallback(async () => {
    setRosterLoading(true);
    try {
      const params = { page: rosterPage, limit: ROSTER_LIMIT };
      if (rosterStatus) params.status = rosterStatus;
      if (rosterQuery) params.q = rosterQuery;
      const { data } = await axios.get(`${base}/checkin/roster`, { params });
      if (data.success) {
        setRoster(data.tickets);
        setRosterTotal(data.total);
        setRosterError(null);
      } else {
        setRosterError(data.message || 'Could not load the roster');
      }
    } catch (err) {
      setRosterError(err?.response?.data?.message || 'Could not load the roster. Check your connection.');
    } finally {
      setRosterLoading(false);
    }
  }, [base, rosterPage, rosterStatus, rosterQuery]);

  useEffect(() => {
    if (tab === 'roster') loadRoster();
  }, [tab, loadRoster]);

  // Tier picker for walk-ins — loaded once, the first time the tab opens.
  useEffect(() => {
    if (tab !== 'walkin' || tiers !== null) return undefined;
    let cancelled = false;
    axios.get(`${base}/checkin/tiers`)
      .then(({ data }) => {
        if (cancelled) return;
        const list = (data && data.success && data.tiers) || [];
        setTiers(list);
        setTiersError(data && data.success ? null : (data?.message || 'Could not load ticket tiers'));
        if (list.length === 1) setWalkinForm((f) => (f.tier_id ? f : { ...f, tier_id: String(list[0].id) }));
      })
      .catch((err) => {
        if (cancelled) return;
        setTiers([]);
        setTiersError(err?.response?.data?.message || 'Could not load ticket tiers');
      });
    return () => { cancelled = true; };
  }, [tab, tiers, base]);

  const handleScan = useCallback(
    async (qrToken) => {
      if (scanBusyRef.current) return;
      scanBusyRef.current = true;
      try {
        let result;
        try {
          const { data, status } = await axios.post(`${base}/checkin/scan`, { qrToken });
          result = toScanResult(data, status);
        } catch (err) {
          result = toScanResult(err?.response?.data, err?.response?.status);
        }
        setScanResult(result);
        vibrate(SCAN_OUTCOMES[result.kind].vibrate);
      } finally {
        setTimeout(() => { scanBusyRef.current = false; }, 1500); // debounce re-scans of the same code
      }
    },
    [base]
  );

  // Lazily creates (and caches) the Html5Qrcode instance bound to #qr-reader.
  const getScanner = useCallback(async () => {
    if (!scannerRef.current) {
      const { Html5Qrcode } = await import('html5-qrcode');
      scannerRef.current = new Html5Qrcode('qr-reader');
    }
    return scannerRef.current;
  }, []);

  const stopCamera = useCallback(async () => {
    const scanner = scannerRef.current;
    if (!scanner || !scanner.isScanning) return;
    try {
      await scanner.stop();
    } catch (err) {
      // already stopped/torn down — nothing to do
    }
    setCameraOn(false);
  }, []);

  const startCamera = useCallback(async () => {
    try {
      const scanner = await getScanner();
      if (scanner.isScanning) return;
      setScanResult(null);
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10 }, // no qrbox — the whole viewport is the scan area
        (decodedText) => handleScan(decodedText),
        () => {} // ignore per-frame scan failures (no code in view)
      );
      setCameraOn(true);
    } catch (err) {
      setCameraOn(false);
      setScanResult({ kind: 'error', detail: 'Camera permission denied or unavailable. Use the Roster tab to search by name.' });
    }
  }, [getScanner, handleScan]);

  const scanImageFile = useCallback(
    async (file) => {
      try {
        const scanner = await getScanner();
        await stopCamera();
        setScanResult(null);
        const decodedText = await scanner.scanFile(file, false);
        handleScan(decodedText);
      } catch (err) {
        setScanResult({ kind: 'error', detail: 'Could not read a QR code from that image' });
      }
    },
    [getScanner, stopCamera, handleScan]
  );

  const handleFileChange = useCallback(
    (e) => {
      const file = e.target.files && e.target.files[0];
      e.target.value = ''; // allow re-selecting the same file later
      if (file) scanImageFile(file);
    },
    [scanImageFile]
  );

  // Stop the camera whenever the Scan tab isn't active, and on unmount.
  useEffect(() => {
    if (tab !== 'scan') stopCamera();
    return () => { stopCamera(); };
  }, [tab, stopCamera]);

  // Publish the Camera/Scan controls to the app footer, only while the Scan
  // tab is showing. HubSectionRoute slots these between its Back/Home buttons.
  useEffect(() => {
    if (typeof onExtraFooterButtons !== 'function') return undefined;
    if (tab === 'scan') {
      onExtraFooterButtons([
        { id: 'checkin-camera', icon: <FaCamera />, label: 'Camera', onClick: startCamera },
        { id: 'checkin-scan-file', icon: <MdDocumentScanner />, label: 'Scan', onClick: () => fileInputRef.current?.click() },
      ]);
    } else {
      onExtraFooterButtons([]);
    }
    return () => onExtraFooterButtons([]);
  }, [tab, onExtraFooterButtons, startCamera]);

  const submitWalkin = async (e) => {
    e.preventDefault();
    setWalkinSaving(true);
    setWalkinMessage(null);
    try {
      const { data } = await axios.post(`${base}/checkin/walkin`, {
        ...walkinForm,
        num_tickets: Number(walkinForm.num_tickets) || 1,
      });
      if (data.success) {
        setWalkinMessage({ ok: true, text: `Checked in — ${data.tickets.length} ticket${data.tickets.length === 1 ? '' : 's'}` });
        setWalkinForm(EMPTY_WALKIN);
      } else {
        setWalkinMessage({ ok: false, text: data.message });
      }
    } catch (err) {
      setWalkinMessage({ ok: false, text: err?.response?.data?.message || 'Walk-in registration failed' });
    } finally {
      setWalkinSaving(false);
    }
  };

  const overrideTicket = async (ticket, status) => {
    setOverrideBusyId(ticket.id);
    setRosterError(null);
    const action = status === 'checked_in' ? 'check in' : 'undo check-in for';
    try {
      const { data } = await axios.post(`${base}/checkin/${ticket.id}/override`, { status });
      if (data && data.success === false) {
        setRosterError(data.message || `Could not ${action} ${ticket.attendee_name}`);
        return;
      }
      await loadRoster();
    } catch (err) {
      setRosterError(
        err?.response?.data?.message ||
          `Could not ${action} ${ticket.attendee_name}. Check your connection and try again.`
      );
    } finally {
      setOverrideBusyId(null);
    }
  };

  const rosterPages = Math.max(1, Math.ceil(rosterTotal / ROSTER_LIMIT));
  const csvParams = new URLSearchParams({ format: 'csv' });
  if (rosterStatus) csvParams.set('status', rosterStatus);
  if (rosterQuery) csvParams.set('q', rosterQuery);
  const outcome = scanResult ? SCAN_OUTCOMES[scanResult.kind] : null;

  return (
    <div className={`max-w-lg mx-auto p-3 checkin-console${tab === 'scan' ? ' checkin-console--scan' : ''}`}>
      <h1 className="text-lg font-semibold mb-3">Check-in</h1>

      <div className="flex gap-2 mb-4 border-b border-white/10">
        {[
          { key: 'scan', label: 'Scan' },
          { key: 'walkin', label: 'Walk-in' },
          { key: 'roster', label: 'Roster' },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.key ? 'border-brand-orange text-brand-orange' : 'border-transparent opacity-60'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'scan' && (
        <div className="checkin-scan-area">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
          <div className="checkin-qr-reader-wrap">
            <div id="qr-reader" className="checkin-qr-reader" />
            {!cameraOn && (
              <div className="checkin-qr-reader__placeholder">
                <FaCamera size={32} />
                <p>Tap <strong>Camera</strong> below to scan a ticket, or <strong>Scan</strong> to read a saved QR image.</p>
              </div>
            )}
          </div>
          {/* Always-mounted live region so screen readers announce each new result. */}
          <div role="status" aria-live="polite" aria-atomic="true" className="mt-3">
            {scanResult && (
              <div
                className={`note ${outcome.note} checkin-scan-result checkin-scan-result--${scanResult.kind}`}
                data-testid="scan-result"
              >
                <div className="checkin-scan-result__title">
                  <span aria-hidden="true">{outcome.icon} </span>{outcome.title}
                </div>
                {scanResult.detail && <div className="checkin-scan-result__detail">{scanResult.detail}</div>}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'walkin' && (
        <form onSubmit={submitWalkin} className="space-y-3">
          {walkinMessage && (
            <div className={`px-3 py-2 rounded text-sm ${walkinMessage.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              {walkinMessage.text}
            </div>
          )}
          <input
            className="w-full px-3 py-2 rounded border bg-transparent"
            placeholder="Full name"
            value={walkinForm.name}
            onChange={(e) => setWalkinForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
          <input
            className="w-full px-3 py-2 rounded border bg-transparent"
            type="email"
            placeholder="Email"
            value={walkinForm.email}
            onChange={(e) => setWalkinForm((f) => ({ ...f, email: e.target.value }))}
            required
          />
          <input
            className="w-full px-3 py-2 rounded border bg-transparent"
            placeholder="Phone (optional)"
            value={walkinForm.phone}
            onChange={(e) => setWalkinForm((f) => ({ ...f, phone: e.target.value }))}
          />
          <select
            className="w-full px-3 py-2 rounded border bg-transparent"
            aria-label="Ticket tier"
            value={walkinForm.tier_id}
            onChange={(e) => setWalkinForm((f) => ({ ...f, tier_id: e.target.value }))}
            disabled={tiers === null}
            required
          >
            <option value="">{tiers === null ? 'Loading ticket tiers…' : 'Choose a ticket tier'}</option>
            {(tiers || []).map((tier) => (
              <option key={tier.id} value={String(tier.id)} disabled={tier.remaining === 0}>
                {tier.name}
                {tier.remaining == null ? '' : tier.remaining === 0 ? ' (sold out)' : ` (${tier.remaining} left)`}
              </option>
            ))}
          </select>
          {tiersError && <div className="note error text-sm">{tiersError}</div>}
          {tiers && tiers.length === 0 && !tiersError && (
            <div className="note warn text-sm">This event has no active ticket tiers. Add one in the ticket settings first.</div>
          )}
          <input
            className="w-full px-3 py-2 rounded border bg-transparent"
            type="number"
            min="1"
            placeholder="Number of tickets"
            value={walkinForm.num_tickets}
            onChange={(e) => setWalkinForm((f) => ({ ...f, num_tickets: e.target.value }))}
          />
          <p className="text-xs opacity-60">
            Walk-in registration is comp/free. For a paid walk-in, use the event's public registration link on this
            device, then scan the resulting ticket.
          </p>
          <button
            type="submit"
            disabled={walkinSaving}
            className="w-full px-4 py-2 rounded bg-cobalt text-white font-semibold disabled:opacity-50"
          >
            {walkinSaving ? 'Checking in…' : 'Check in walk-in'}
          </button>
        </form>
      )}

      {tab === 'roster' && (
        <div>
          <input
            type="search"
            className="w-full px-3 py-2 rounded border bg-transparent mb-2"
            placeholder="Search name or email"
            aria-label="Search attendees by name or email"
            value={rosterSearch}
            onChange={(e) => setRosterSearch(e.target.value)}
          />
          <select
            className="w-full px-3 py-2 rounded border bg-transparent mb-3"
            aria-label="Filter by status"
            value={rosterStatus}
            onChange={(e) => { setRosterStatus(e.target.value); setRosterPage(1); }}
          >
            <option value="">All statuses</option>
            <option value="active">Not checked in</option>
            <option value="checked_in">Checked in</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs opacity-60">{rosterTotal} total</div>
            <a
              href={`${base}/checkin/roster?${csvParams.toString()}`}
              className="text-xs px-2 py-1 rounded border border-white/15"
            >
              Export CSV
            </a>
          </div>
          {rosterError && (
            <div className="note error text-sm mb-2" role="alert">{rosterError}</div>
          )}
          {!rosterLoading && !rosterError && roster.length === 0 && (
            <div className="note info text-sm mb-2">
              {rosterQuery ? `No attendees match “${rosterQuery}”.` : 'No tickets yet.'}
            </div>
          )}
          <ul className="space-y-2">
            {roster.map((ticket) => (
              <li key={ticket.id} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 p-3 text-sm">
                <div>
                  <div className="font-semibold">{ticket.attendee_name}</div>
                  <div className="opacity-70 text-xs">{ticket.tier_name} · {ticket.status}</div>
                </div>
                {ticket.status === 'checked_in' ? (
                  <button
                    type="button"
                    onClick={() => overrideTicket(ticket, 'active')}
                    disabled={overrideBusyId === ticket.id}
                    className="px-3 py-2 rounded border text-xs disabled:opacity-50"
                  >
                    Undo
                  </button>
                ) : ticket.status === 'active' ? (
                  <button
                    type="button"
                    onClick={() => overrideTicket(ticket, 'checked_in')}
                    disabled={overrideBusyId === ticket.id}
                    className="px-3 py-2 rounded bg-cobalt text-white text-xs disabled:opacity-50"
                  >
                    Check in
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
          {rosterPages > 1 && (
            <nav className="flex items-center justify-between mt-3 text-sm" aria-label="Roster pages">
              <button
                type="button"
                onClick={() => setRosterPage((p) => Math.max(1, p - 1))}
                disabled={rosterPage <= 1 || rosterLoading}
                className="px-3 py-2 rounded border disabled:opacity-40"
              >
                Previous
              </button>
              <span className="opacity-70">Page {rosterPage} of {rosterPages}</span>
              <button
                type="button"
                onClick={() => setRosterPage((p) => Math.min(rosterPages, p + 1))}
                disabled={rosterPage >= rosterPages || rosterLoading}
                className="px-3 py-2 rounded border disabled:opacity-40"
              >
                Next
              </button>
            </nav>
          )}
        </div>
      )}
    </div>
  );
}
